import React, { useEffect, useRef } from 'react'
import { useDialogPortal } from '../../Provider/DialogPortalContext.js'

export type BaseModalProps<Result = void> = {
  isOpen: boolean
  onClose: (result?: Result) => void
  children: React.ReactNode
  className?: string
  testId?: string
  /**
   * False for a modal whose content uses Escape itself and holds unsaved work (the data grid, where it
   * cancels a cell edit): Escape then never closes it, and the explicit Cancel/close buttons do.
   */
  closeOnEscape?: boolean
}

/**
 * This component is the basis for implementing modals in spcfy.
 * It makes use of the general infrastructure provided by the other files in this package
 * to implement opening, closing and result passing in a basic layout way.
 *
 * @param isOpen
 * @param onClose
 * @param children
 * @param className
 * @param testId
 * @constructor
 */
export function BaseModal<Result = void>({
  isOpen,
  onClose,
  children,
  className = '',
  testId,
  closeOnEscape = true
}: BaseModalProps<Result>) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const { setDialogRef, dialogRef: activeDialogRef } = useDialogPortal()

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen && !dialog.open) {
      setDialogRef(dialogRef)
      dialog.showModal()
    } else if (!isOpen && dialog.open) {
      if (activeDialogRef === dialogRef) {
        setDialogRef(null)
      }
      dialog.close()
    }

    return () => {
      if (activeDialogRef === dialogRef) {
        setDialogRef(null)
      }
    }
  }, [isOpen])

  useEffect(() => {
    /* Only the open (topmost) modal answers Escape; the ones stacked beneath it stay mounted. */
    if (!isOpen || !closeOnEscape) {
      return
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        /* Cancels the browser's own close request, whose `cancel` event would close this a second time
           and so pop the modal (and result listener) beneath it as well. */
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, isOpen, closeOnEscape])

  if (!isOpen) return null

  return (
    <dialog
      ref={dialogRef}
      className={`modal backdrop:bg-black/25 ${className}`}
      data-testid={testId}
      onCancel={(e) => {
        e.preventDefault()
        if (closeOnEscape) {
          onClose()
        }
      }}
      onKeyDown={(e) => {
        /* Stops the browser from closing the dialog natively, which it may do on a repeated Escape even
           though `cancel` is prevented; the content (e.g. a grid cell editor) still sees the key. */
        if (e.key === 'Escape' && !closeOnEscape) {
          e.preventDefault()
        }
      }}
    >
      {/*
       * `children` must stay a DIRECT child: daisyUI reveals `.modal-box` through a direct-child
       * selector, so any wrapper element here leaves every modal in the app rendered but at
       * opacity 0. `.modal` itself supplies the fixed full-viewport box, the stacking and the
       * centering the old wrapper did; only its 25% dim needs restating, since daisyUI's own
       * `::backdrop` is 10%.
       */}
      {children}
    </dialog>
  )
}
